import React, { useState, useEffect } from 'react';
import pb from '../../../lib/pocketbase';
import './AccountDashboard.css';

const AccountDashboard = ({ user }) => {
    const [ongoingIntervention, setOngoingIntervention] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);

    useEffect(() => {
        if (!user?.id) return;

        const fetchData = async () => {
            try {
                // Fetch the most recent quote marked as 'in_progress' or just the latest one
                const records = await pb.collection('quotes').getList(1, 1, {
                    filter: `users = "${user.id}"`,
                    sort: '-created',
                });

                if (records.items.length > 0) {
                    setOngoingIntervention(records.items[0]);
                }

                // Fetch notifications
                try {
                    const notifs = await pb.collection('notifications').getList(1, 10, {
                        filter: `users = "${user.id}"`,
                        sort: '-created',
                    });
                    setNotifications(notifs.items);
                } catch (e) {
                    // Fail silently if collection doesn't exist yet
                }

            } catch (err) {
                console.error('Erreur dashboard:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user?.id]);

    const statusMap = {
        en_attente: { label: 'En attente de validation', icon: '⏳', progress: 10 },
        valide: { label: 'Devis validé', icon: '✅', progress: 30 },
        diagnostic: { label: 'Diagnostic en cours', icon: '💻', progress: 50 },
        installation: { label: 'Reprogrammation / Installation', icon: '⛽', progress: 75 },
        test: { label: 'Tests finaux', icon: '🛣️', progress: 90 },
        pret: { label: 'Véhicule prêt', icon: '🏁', progress: 100 },
        termine: { label: 'Intervention terminée', icon: '✨', progress: 100 },
    };

    if (!user) return <div className="account-content-wrapper">Veuillez vous connecter.</div>;

    if (loading) return <div className="account-content-wrapper">Chargement du tableau de bord...</div>;

    const currentStatus = ongoingIntervention ? (statusMap[ongoingIntervention.status] || statusMap.en_attente) : null;

    return (
        <div className="account-content-wrapper">
            <section className="dashboard-hero">
                <div className="hero-text">
                    <h2>Ravi de vous revoir, {user?.name.split(' ')[0]} 👋</h2>
                    <p>{ongoingIntervention ? 'Voici le suivi de votre véhicule.' : 'Bienvenue dans votre espace client.'}</p>
                </div>
            </section>

            {ongoingIntervention && ongoingIntervention.status !== 'termine' && (
                <section className="ongoing-task-card">
                    <div className="task-header">
                        <div className="live-badge">LIVE</div>
                        <h3>Suivi en temps réel</h3>
                    </div>

                    <div className="task-vehicle-info">
                        <strong>{ongoingIntervention.vehicle_brand} {ongoingIntervention.vehicle_model} ({ongoingIntervention.vehicle_plate})</strong>
                        <span>{ongoingIntervention.service_key ? 'Reproduction de clé' : ''} {ongoingIntervention.service_ethanol ? 'Ethanol' : ''} {ongoingIntervention.service_diagnostic ? 'Diagnostic' : ''}</span>
                    </div>

                    <div className="progress-container">
                        <div className="progress-bar-wrapper">
                            <div className="progress-fill" style={{ width: `${currentStatus.progress}%` }}></div>
                            <div className="progress-glow"></div>
                        </div>
                        <div className="progress-steps-labels">
                            <span className={['diagnostic', 'installation', 'test', 'pret', 'termine'].includes(ongoingIntervention.status) ? 'done' : (ongoingIntervention.status === 'en_attente' || ongoingIntervention.status === 'valide' ? 'active' : '')}>Attente/Valide</span>
                            <span className={['installation', 'test', 'pret', 'termine'].includes(ongoingIntervention.status) ? 'done' : (ongoingIntervention.status === 'diagnostic' ? 'active' : '')}>Diag</span>
                            <span className={['test', 'pret', 'termine'].includes(ongoingIntervention.status) ? 'done' : (ongoingIntervention.status === 'installation' ? 'active' : '')}>Pose</span>
                            <span className={ongoingIntervention.status === 'pret' || ongoingIntervention.status === 'termine' ? 'done' : ''}>Prêt</span>
                        </div>
                    </div>

                    <div className="task-footer">
                        <div className="current-status-info">
                            <span className="status-icon-mini">{currentStatus.icon}</span>
                            <span>{currentStatus.label}</span>
                        </div>
                        {ongoingIntervention.estimated_time && (
                            <div className="time-estimate">
                                Fin estimée : <strong>{ongoingIntervention.estimated_time}</strong>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <div className="dashboard-grid-layout-account">
                <div className="dashboard-main-col">
                    <div className="dashboard-grid-account">
                        <div className="card-mini">
                            <div className="mini-icon blue">🔑</div>
                            <div className="mini-content">
                                <span className="mini-value">{ongoingIntervention?.service_key ? '1' : '0'}</span>
                                <span className="mini-label">Clé enregistrée</span>
                            </div>
                        </div>
                        <div className="card-mini">
                            <div className="mini-icon green">⛽</div>
                            <div className="mini-content">
                                <span className="mini-value">{ongoingIntervention?.service_ethanol ? 'Actif' : 'Inactif'}</span>
                                <span className="mini-label">Optimisation E85</span>
                            </div>
                        </div>
                        <div className="card-mini">
                            <div className="mini-icon purple">📅</div>
                            <div className="mini-content">
                                <span className="mini-value">Prochain RDV</span>
                                <span className="mini-label">Bientôt disponible</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dashboard-side-col">
                    <section className="notifications-card">
                        <div className="card-header-flex">
                            <h3>Notifications</h3>
                        </div>
                        <div className="notification-list-account">
                            {notifications.length > 0 ? notifications.map(notif => (
                                <div key={notif.id} className={`notification-item-account ${notif.read ? '' : 'new'}`}>
                                    {!notif.read && <div className="notif-dot"></div>}
                                    <div className="notif-content">
                                        <p>{notif.message}</p>
                                        <span>{new Date(notif.created).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            )) : (
                                <div className="notification-item-account">
                                    <div className="notif-content">
                                        <p>Aucune nouvelle notification.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default AccountDashboard;

